import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { useAdminAuth, formatApiErrorDetail } from "../../lib/adminAuth";
import { useSiteContent } from "../../lib/siteContent";

const EMPTY_BI = { tr: "", en: "" };

function mergeBi(target, src) {
  return { ...EMPTY_BI, ...(target || {}), ...(src || {}) };
}

const DEFAULT_PILLARS = [
  { tr: "In Touch with Self", en: "In Touch with Self" },
  { tr: "In Touch with Others", en: "In Touch with Others" },
  { tr: "In Touch with Work", en: "In Touch with Work" },
  { tr: "Dayanıklılık", en: "Resilience" },
];

const DEFAULT_WHY = [
  { tr: "Bilimden besleniyoruz", en: "Rooted in science" },
  { tr: "Deneyimsel öğrenme", en: "Experiential learning" },
  { tr: "Her kurum biriciktir", en: "Every organisation is unique" },
  { tr: "Bütünsel bakış", en: "Holistic perspective" },
  { tr: "Performans ile psikoloji çatışmaz", en: "Performance and psychology align" },
  { tr: "Akademiyi sahaya indiriyoruz", en: "Bringing academia to the field" },
];

function makeDefault(home) {
  const h = home || {};
  const pillarsArr = (h.pillars && h.pillars.length > 0) ? h.pillars : [];
  const whyArr = (h.whyItems && h.whyItems.length > 0) ? h.whyItems : [];

  return {
    hero: {
      eyebrow: mergeBi({}, h.hero?.eyebrow),
      title: mergeBi({}, h.hero?.title),
      lead: mergeBi({}, h.hero?.lead),
      ctaPrimary: mergeBi({}, h.hero?.ctaPrimary),
      ctaSecondary: mergeBi({}, h.hero?.ctaSecondary),
    },
    stats: {
      programs: { value: h.stats?.programs?.value ?? "250", suffix: h.stats?.programs?.suffix ?? "+", label: mergeBi({}, h.stats?.programs?.label) },
      clients: { value: h.stats?.clients?.value ?? "50", suffix: h.stats?.clients?.suffix ?? "+", label: mergeBi({}, h.stats?.clients?.label) },
      participants: { value: h.stats?.participants?.value ?? "10", suffix: h.stats?.participants?.suffix ?? "K+", label: mergeBi({}, h.stats?.participants?.label) },
      years: { value: h.stats?.years?.value ?? "15", suffix: h.stats?.years?.suffix ?? "+", label: mergeBi({}, h.stats?.years?.label) },
    },
    philosophy: {
      eyebrow: mergeBi({}, h.philosophy?.eyebrow),
      title: mergeBi({}, h.philosophy?.title),
      body: mergeBi({}, h.philosophy?.body),
    },
    pillars: DEFAULT_PILLARS.map((def, i) => ({
      title: mergeBi(def, pillarsArr[i]?.title),
      description: mergeBi({}, pillarsArr[i]?.description),
    })),
    why: {
      eyebrow: mergeBi({}, h.why?.eyebrow),
      title: mergeBi({}, h.why?.title),
    },
    whyItems: DEFAULT_WHY.map((def, i) => ({
      title: mergeBi(def, whyArr[i]?.title),
      description: mergeBi({}, whyArr[i]?.description),
    })),
    finalCta: {
      title: mergeBi({}, h.finalCta?.title),
      body: mergeBi({}, h.finalCta?.body),
    },
  };
}

export default function AdminHome() {
  const { authedClient } = useAdminAuth();
  const { refresh: refreshSite } = useSiteContent();
  const [home, setHome] = useState(null);
  const [services, setServices] = useState([]);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedClient.get("/admin/site");
      setHome(makeDefault(data.home));
      setServices(data.services || []);
      setContact(data.contact || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [authedClient]);

  useEffect(() => { load(); }, [load]);

  const setBi = (section, field) => (lang, v) =>
    setHome((p) => ({ ...p, [section]: { ...p[section], [field]: { ...p[section][field], [lang]: v } } }));

  const setStat = (k, prop) => (val) =>
    setHome((p) => ({ ...p, stats: { ...p.stats, [k]: { ...p.stats[k], [prop]: val } } }));

  const setStatLabel = (k) => (lang, v) =>
    setHome((p) => ({ ...p, stats: { ...p.stats, [k]: { ...p.stats[k], label: { ...p.stats[k].label, [lang]: v } } } }));

  const setPillar = (i, field) => (lang, v) =>
    setHome((p) => {
      const pillars = [...p.pillars];
      pillars[i] = { ...pillars[i], [field]: { ...pillars[i][field], [lang]: v } };
      return { ...p, pillars };
    });

  const setWhyItem = (i, field) => (lang, v) =>
    setHome((p) => {
      const whyItems = [...p.whyItems];
      whyItems[i] = { ...whyItems[i], [field]: { ...whyItems[i][field], [lang]: v } };
      return { ...p, whyItems };
    });

  const save = async () => {
    setSaving(true);
    try {
      await authedClient.put("/admin/site", { home, services, contact });
      toast.success("Anasayfa içeriği kaydedildi");
      refreshSite();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !home) return <div className="text-ink/55 text-sm py-12 text-center">Yükleniyor…</div>;

  return (
    <div data-testid="admin-home-page">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-7">
        <div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Anasayfa</h1>
          <p className="mt-1 text-sm text-ink/60">Anasayfa üzerindeki tüm metinleri Türkçe ve İngilizce olarak düzenleyin.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="home-save" className="btn-primary disabled:opacity-60">
          <Save className="w-4 h-4" strokeWidth={1.5} />
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </header>

      {/* HERO */}
      <Section title="Hero (Üst Bölüm)" testid="hero">
        <BilingualField label="Üst etiket (eyebrow)" value={home.hero.eyebrow} onChange={setBi("hero", "eyebrow")} testid="home-hero-eyebrow" />
        <BilingualField label="Başlık" value={home.hero.title} multiline rows={3} onChange={setBi("hero", "title")} testid="home-hero-title" />
        <p className="text-xs text-ink/50 -mt-3">Son iki kelime otomatik olarak vurgulanır (italik turuncu).</p>
        <BilingualField label="Açıklama metni" value={home.hero.lead} multiline rows={4} onChange={setBi("hero", "lead")} testid="home-hero-lead" />
        <div className="grid md:grid-cols-2 gap-5">
          <BilingualField label="Birincil buton" value={home.hero.ctaPrimary} onChange={setBi("hero", "ctaPrimary")} testid="home-hero-cta1" />
          <BilingualField label="İkincil buton" value={home.hero.ctaSecondary} onChange={setBi("hero", "ctaSecondary")} testid="home-hero-cta2" />
        </div>
      </Section>

      {/* STATS */}
      <Section title="İstatistikler" testid="stats">
        <div className="grid md:grid-cols-2 gap-5">
          {["programs", "clients", "participants", "years"].map((k) => (
            <div key={k} className="rounded-xl border border-cream-300/60 bg-cream-50 p-5 space-y-3">
              <div className="grid grid-cols-[1fr_80px] gap-2">
                <div>
                  <span className="admin-label">Sayı</span>
                  <input data-testid={`home-stat-${k}-value`} value={home.stats[k].value} onChange={(e) => setStat(k, "value")(e.target.value)} className="admin-input" />
                </div>
                <div>
                  <span className="admin-label">Son ek</span>
                  <input data-testid={`home-stat-${k}-suffix`} value={home.stats[k].suffix} onChange={(e) => setStat(k, "suffix")(e.target.value)} className="admin-input" />
                </div>
              </div>
              <BilingualField label="Etiket" value={home.stats[k].label} onChange={setStatLabel(k)} testid={`home-stat-${k}-label`} />
            </div>
          ))}
        </div>
      </Section>

      {/* PHILOSOPHY */}
      <Section title="Yaklaşım — Başlık & Açıklama" testid="philosophy">
        <BilingualField label="Üst etiket" value={home.philosophy.eyebrow} onChange={setBi("philosophy", "eyebrow")} testid="home-phil-eyebrow" />
        <BilingualField label="Başlık" value={home.philosophy.title} onChange={setBi("philosophy", "title")} testid="home-phil-title" />
        <BilingualField label="Açıklama" value={home.philosophy.body} multiline rows={4} onChange={setBi("philosophy", "body")} testid="home-phil-body" />
      </Section>

      {/* PILLARS */}
      <Section title="Yaklaşım — 4 Kart (Pillars)" testid="pillars">
        <p className="text-xs text-ink/50 -mt-2">Her kartın başlığını ve açıklama metnini düzenleyebilirsiniz.</p>
        {home.pillars.map((pillar, i) => (
          <div key={i} className="rounded-xl border border-cream-300/60 bg-cream-50 p-5 space-y-3">
            <span className="text-xs uppercase tracking-widest text-terracotta-400 font-medium">Kart {i + 1}</span>
            <BilingualField label="Başlık" value={pillar.title} onChange={setPillar(i, "title")} testid={`home-pillar-${i}-title`} />
            <BilingualField label="Açıklama" value={pillar.description} multiline rows={3} onChange={setPillar(i, "description")} testid={`home-pillar-${i}-desc`} />
          </div>
        ))}
      </Section>

      {/* WHY */}
      <Section title="Neden inTouch? — Başlık" testid="why">
        <BilingualField label="Üst etiket" value={home.why.eyebrow} onChange={setBi("why", "eyebrow")} testid="home-why-eyebrow" />
        <BilingualField label="Başlık" value={home.why.title} onChange={setBi("why", "title")} testid="home-why-title" />
      </Section>

      {/* WHY ITEMS */}
      <Section title="Neden inTouch? — 6 Kart" testid="why-items">
        <p className="text-xs text-ink/50 -mt-2">Her kartın başlığını ve açıklama metnini düzenleyebilirsiniz.</p>
        {home.whyItems.map((item, i) => (
          <div key={i} className="rounded-xl border border-cream-300/60 bg-cream-50 p-5 space-y-3">
            <span className="text-xs uppercase tracking-widest text-terracotta-400 font-medium">Kart {i + 1}</span>
            <BilingualField label="Başlık" value={item.title} onChange={setWhyItem(i, "title")} testid={`home-why-${i}-title`} />
            <BilingualField label="Açıklama" value={item.description} multiline rows={3} onChange={setWhyItem(i, "description")} testid={`home-why-${i}-desc`} />
          </div>
        ))}
      </Section>

      {/* FINAL CTA */}
      <Section title="Son Bölüm (Final CTA)" testid="final">
        <BilingualField label="Başlık" value={home.finalCta.title} multiline rows={2} onChange={setBi("finalCta", "title")} testid="home-final-title" />
        <BilingualField label="Açıklama" value={home.finalCta.body} multiline rows={3} onChange={setBi("finalCta", "body")} testid="home-final-body" />
      </Section>

      <div className="sticky bottom-4 mt-8 flex justify-end">
        <button onClick={save} disabled={saving} className="btn-primary shadow-float disabled:opacity-60">
          <Save className="w-4 h-4" strokeWidth={1.5} />
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <section data-testid={`admin-home-section-${testid}`} className="surface-card p-7 md:p-9 mb-5 space-y-5">
      <h2 className="font-serif text-2xl text-ink">{title}</h2>
      {children}
    </section>
  );
}

function BilingualField({ label, value, onChange, multiline = false, rows = 3, testid = "" }) {
  return (
    <div>
      <span className="admin-label">{label}</span>
      <div className="grid md:grid-cols-2 gap-3">
        {["tr", "en"].map((lang) => (
          <div key={lang}>
            <span className="block text-[10px] uppercase tracking-widest text-ink/45 mb-1">{lang.toUpperCase()}</span>
            {multiline ? (
              <textarea
                data-testid={`${testid}-${lang}`}
                rows={rows}
                value={value?.[lang] || ""}
                onChange={(e) => onChange(lang, e.target.value)}
                className="admin-textarea"
              />
            ) : (
              <input
                data-testid={`${testid}-${lang}`}
                value={value?.[lang] || ""}
                onChange={(e) => onChange(lang, e.target.value)}
                className="admin-input"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
